import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Alert,
  Divider,
  CardHeader,
  Avatar,
  Skeleton,
  Stack,
  Paper,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { taskService } from '../../services/taskService';
import { Task } from '../../types/task';
import { translateCategory } from '../../utils/categoryTranslations';
import { translateTag } from '../../utils/tagTranslations';
import TaskFilters, { TaskFiltersValues } from '../../components/TaskFilters';
import { formatCurrency } from '../../utils/currency';
import PageBackButton from '../../components/PageBackButton';

const TaskList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [lastFilters, setLastFilters] = useState<TaskFiltersValues>({
    search: '',
    category_id: '',
    status: '',
    min_budget: '',
    max_budget: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  const fetchTasks = async (filters: TaskFiltersValues) => {
    try {
      setLoading(true);
      setLastFilters(filters);
      
      // Подготовка на параметрите
      const params: any = {};
      if (filters.search) params.search = filters.search;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.status) params.status = filters.status;
      if (filters.min_budget) params.min_budget = filters.min_budget;
      if (filters.max_budget) params.max_budget = filters.max_budget;
      if (filters.sort_by) params.sort_by = filters.sort_by;
      if (filters.sort_order) params.sort_order = filters.sort_order;
      
      const data = onlyFavorites
        ? await taskService.getFavoriteTasks()
        : await taskService.getAvailableTasks(params);

      const filteredData = onlyFavorites
        ? data.filter((task) => {
            if (filters.search) {
              const searchText = filters.search.toLowerCase();
              const inTitle = task.title.toLowerCase().includes(searchText);
              const inDescription = task.description.toLowerCase().includes(searchText);
              if (!inTitle && !inDescription) {
                return false;
              }
            }
            if (filters.category_id && String(task.category_id || '') !== String(filters.category_id)) {
              return false;
            }
            if (filters.status && task.status !== filters.status) {
              return false;
            }
            if (filters.min_budget && Number(task.budget) < Number(filters.min_budget)) {
              return false;
            }
            if (filters.max_budget && Number(task.budget) > Number(filters.max_budget)) {
              return false;
            }
            return true;
          })
        : data;

      const sortedData = [...filteredData].sort((left, right) => {
        const direction = filters.sort_order === 'asc' ? 1 : -1;
        if (filters.sort_by === 'budget') {
          return (Number(left.budget) - Number(right.budget)) * direction;
        }
        if (filters.sort_by === 'deadline') {
          return (new Date(left.deadline).getTime() - new Date(right.deadline).getTime()) * direction;
        }
        return (new Date(left.created_at).getTime() - new Date(right.created_at).getTime()) * direction;
      });
      setTasks(sortedData);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(t('common.error_try_again'));
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromQuery = params.get('category_id') || '';

    // Начално зареждане (вкл. категория от URL, ако има)
    const initialFilters: TaskFiltersValues = {
      search: '',
      category_id: categoryFromQuery,
      status: '',
      min_budget: '',
      max_budget: '',
      sort_by: 'created_at',
      sort_order: 'desc',
    };
    fetchTasks(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleFiltersChange = (filters: TaskFiltersValues) => {
    fetchTasks(filters);
  };

  const getDateLocale = () => getDateFnsLocale(i18n.language);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return t('common.unknown_date');
      const date = new Date(dateString);
      // Проверка дали датата е валидна
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date: ${dateString}`);
        return t('common.unknown_date');
      }
      return format(date, 'PPP', { locale: getDateLocale() });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('common.unknown_date');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="task-list-page">
      <PageBackButton />
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0 }}>
          {t('tasks.list.title')}
          </Typography>
          <Button
            variant={onlyFavorites ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              const nextValue = !onlyFavorites;
              setOnlyFavorites(nextValue);
              fetchTasks(lastFilters);
            }}
          >
            {onlyFavorites ? t('tasks.list.show_all_tasks') : t('tasks.list.show_favorite_tasks')}
          </Button>
        </Box>
        {!loading && (
          <Typography variant="body2" color="text.secondary">
            {tasks.length} {t('common.results', 'резултата')}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchTasks(lastFilters)}>
              {t('common.retry', 'Опитай отново')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Task Filters */}
      <TaskFilters onFiltersChange={handleFiltersChange} userType="professional" />

      {loading ? (
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {[...Array(6)].map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" />
                      <Skeleton width="40%" />
                    </Box>
                  </Box>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="rounded" height={34} />
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : tasks.length === 0 ? (
        <Paper variant="outlined" sx={{ mt: 2, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('common.no_results')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tasks.list.try_different_filters', 'Пробвай с различни филтри или по-обща дума за търсене.')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {tasks.map((task) => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 6
                }
              }} data-testid="task-card">
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: task.category?.id ? `hsl(${task.category.id * 30}, 70%, 50%)` : 'primary.main' }}>
                      {task.category?.name.charAt(0) || 'T'}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" component="div" noWrap>
                      {task.title}
                    </Typography>
                  }
                  subheader={formatDate(task.created_at)}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {task.description}
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {formatCurrency(task.budget, i18n.language, task.currency)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2" noWrap>
                          {formatDate(task.deadline)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2" noWrap>
                          {task.address}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {task.category && (
                      <Chip 
                        size="small" 
                        label={translateCategory(task.category.name, i18n.language)} 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                    {task.tags?.slice(0, 2).map((tag) => (
                      <Chip 
                        key={tag.id} 
                        size="small" 
                        label={translateTag(tag.name, i18n.language)} 
                        variant="outlined"
                      />
                    ))}
                    {task.tags && task.tags.length > 2 && (
                      <Chip 
                        size="small" 
                        label={`+${task.tags.length - 2}`} 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                  >
                    {t('tasks.list.card.view_details')}
                  </Button>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                    <Chip
                      size="small"
                      label={t(`tasks.list.filter.${task.status}`)}
                      color={
                        task.status === 'open' ? 'success' :
                        task.status === 'in_progress' ? 'warning' :
                        task.status === 'completed' ? 'primary' : 'error'
                      }
                    />
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TaskList; 